/**
 * Left-nav expansion resolver — maps the current route + active page key to the
 * ordered set of nav-level activations/deactivations the sidebar applies as the
 * user navigates. Context levels (customer / siteGroup / site) are mutually
 * exclusive and toggle as the user moves between scope routes; staff, dashboard,
 * and settings levels are activated sticky (never auto-collapsed here).
 *
 * Contains:
 * - NAV_OP — activate/deactivate op-type constants
 * - resolveNavLevel — pure route→nav-op resolver (named export)
 *
 * Returns plain JS — no JSX, no redux.
 */

export const NAV_OP = Object.freeze({ ACTIVATE: 'activate', DEACTIVATE: 'deactivate' });

const startsWith = (value, prefix) => typeof value === 'string' && value.startsWith(prefix);

/**
 * @param {string} pathname - location.pathname of the active route.
 * @param {string} page     - the active page key (LeftNav `page` prop).
 * @returns {{ action: string, level: string }[]} Ordered nav ops to dispatch, in
 *   the exact order the sidebar applies them. Order between an activate and a
 *   deactivate of the same level is load-bearing (a context route can deactivate
 *   a level that a page-prefix branch then re-activates).
 */
export function resolveNavLevel(pathname, page) {
  const ops = [];
  const activate = level => ops.push({ action: NAV_OP.ACTIVATE, level });
  const deactivate = level => ops.push({ action: NAV_OP.DEACTIVATE, level });

  const path = pathname;
  const isOnSite = path.includes('/structure/');
  const isOnSiteGroup = !isOnSite && path.includes('/plant/');
  const isOnCustomer = !isOnSite && !isOnSiteGroup && path.startsWith('/c/');

  if (isOnSite) {
    activate('site');
    deactivate('siteGroup');
    deactivate('customer');
  } else if (isOnSiteGroup) {
    activate('siteGroup');
    deactivate('site');
    deactivate('customer');
  } else if (isOnCustomer) {
    activate('customer');
    deactivate('siteGroup');
    deactivate('site');
  }

  const isContextRoute = isOnCustomer || isOnSiteGroup || isOnSite;
  if (startsWith(page, 'support') && !isContextRoute) {
    activate('support');
  } else if (startsWith(page, 'services')) {
    activate('services');
  } else if (startsWith(page, 'plant') || startsWith(page, 'siteGroup')) {
    activate('siteGroup');
  } else if (startsWith(page, 'structure') || startsWith(page, 'site')) {
    activate('site');
    deactivate('siteGroup');
    deactivate('customer');
  } else if (isOnSiteGroup) {
    activate('siteGroup');
    deactivate('site');
    deactivate('customer');
  } else if (isOnCustomer) {
    activate('customer');
    deactivate('siteGroup');
    deactivate('site');
  }

  if (startsWith(page, 'support')) activate('support');
  if (startsWith(page, 'services')) activate('services');
  if (startsWith(page, 'admin')) activate('admin');

  if (page && page.endsWith('_dashboard')) activate('dashboards');

  if (page === 'customer_settings') activate('customerSettings');
  if (page === 'siteGroup_settings') activate('siteGroupSettings');
  if (page === 'structure_settings') activate('siteSettings');

  return ops;
}
